import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Grid,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import TableComponent from 'components/Table';
import { TFunction } from 'i18next';
import React from 'react';
import { AccountingInvolvedParty } from 'v2-types/order';

const COARSE_POINTER = '@media (hover: none) and (pointer: coarse)';

const useStyles = makeStyles((theme: any) => ({
  actions: {
    display: 'flex',
    alignItems: 'center',
    '& .Mui-disabled svg': { color: theme.palette.action.disabled },
  },
  tableActions: {
    justifyContent: 'flex-end',
    gap: theme.spacing(0.5),
    '& button': { padding: theme.spacing(0.5) },
    // DESIGN.md §9: 44px row actions on touch devices; mouse keeps density.
    [COARSE_POINTER]: {
      '& button': { minWidth: 44, minHeight: 44 },
    },
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    minWidth: 0,
    padding: theme.spacing(1.5, 2),
    borderColor: theme.palette.border.card,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  cardTitle: {
    flex: 1,
    minWidth: 0,
    fontWeight: 500,
    color: theme.palette.primary.main,
    overflowWrap: 'anywhere',
  },
  cardActions: {
    flexShrink: 0,
    gap: theme.spacing(0.5),
    '& button': { minWidth: 44, minHeight: 44 },
  },
  editIcon: { color: theme.palette.secondary.main },
  deleteIcon: { color: theme.palette.error.main },
  cardFields: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.border.card}`,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  fieldAmount: {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
  fieldLabel: { color: theme.palette.text.light },
}));

type TableEntriesProps = {
  purchasePriceEntries: AccountingInvolvedParty[];
  t: TFunction;
  setPartyLedgerEntry: (entry: AccountingInvolvedParty | undefined) => void;
  setOpenPartyLedgerModal: (open: boolean) => void;
  onRemoveOrderPartyEntry: (entry: AccountingInvolvedParty) => void;
  isMobile?: boolean;
  disabled?: boolean;
};

const formatAmount = (amount: number | string) => `$${Number(amount).toLocaleString('en-US')}`;

export const TableEntries = ({
  purchasePriceEntries,
  t,
  setPartyLedgerEntry,
  setOpenPartyLedgerModal,
  onRemoveOrderPartyEntry,
  isMobile = false,
  disabled = false,
}: TableEntriesProps) => {
  const classes = useStyles();

  const handleEdit = (entry: AccountingInvolvedParty) => {
    setPartyLedgerEntry(entry);
    setOpenPartyLedgerModal(true);
  };

  const renderActions = (entry: AccountingInvolvedParty, className: string) => (
    <Box className={`${classes.actions} ${className}`}>
      <IconButton
        aria-label={`${t('common:edit')} ${entry.name}`}
        disabled={disabled}
        onClick={() => handleEdit(entry)}
        size="large"
      >
        <FontAwesomeIcon icon="edit" size="xs" className={classes.editIcon} />
      </IconButton>
      <IconButton
        aria-label={`${t('common:delete')} ${entry.name}`}
        disabled={disabled}
        onClick={() => onRemoveOrderPartyEntry(entry)}
        size="large"
      >
        <FontAwesomeIcon icon="trash" size="xs" className={classes.deleteIcon} />
      </IconButton>
    </Box>
  );

  if (isMobile) {
    return (
      <Box className={classes.cardList}>
        {purchasePriceEntries.map((entry, index) => {
          const titleId = `earnest-commissions-entry-${entry._id}-${entry.kind}-${index}`;
          return (
            <Paper
              key={titleId}
              variant="outlined"
              role="group"
              aria-labelledby={titleId}
              className={classes.card}
            >
              <Box className={classes.cardHeader}>
                <Typography id={titleId} variant="subtitle2" className={classes.cardTitle}>
                  {entry.name}
                </Typography>
                {renderActions(entry, classes.cardActions)}
              </Box>
              <Box className={classes.cardFields}>
                <Box className={classes.field}>
                  <Typography variant="caption" className={classes.fieldLabel}>
                    {t('accounting:kind')}
                  </Typography>
                  <Typography variant="body2">
                    {entry.kind}
                  </Typography>
                </Box>
                <Box className={`${classes.field} ${classes.fieldAmount}`}>
                  <Typography variant="caption" className={classes.fieldLabel}>
                    {t('accounting:amount')}
                  </Typography>
                  <Typography variant="body2">{formatAmount(entry.amount)}</Typography>
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Box>
    );
  }

  return (
    <Grid item xs>
      <TableComponent
        disabledBorderLeft
        cellAlignment="left"
        columns={{
          name: {
            header: { title: t('accounting:holder') },
            renderer: ({ row }) => <>{row.name}</>,
          },
          kind: {
            header: { title: t('accounting:kind') },
            renderer: ({ row }) => <>{row.kind}</>,
          },
          amount: {
            header: { title: t('accounting:amount') },
            renderer: ({ row }) => <>{formatAmount(row.amount)}</>,
          },
          actions: {
            header: { title: t('accounting:actions'), align: 'right' },
            renderer: ({ row }) => renderActions(row as AccountingInvolvedParty, classes.tableActions),
          },
        }}
        dataSet={purchasePriceEntries}
      />
    </Grid>
  );
};
